import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { MapPin, Camera, Send, LayoutGrid,
  Users, RefreshCw, Settings, Calculator, LogIn, LogOut, BarChart2, Target,
  FileSpreadsheet, UserCog, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { labelPapel, corPapel } from '../permissoes';
import LoginModal from './LoginModal';

// Cada item carrega a ÁREA a que pertence. O menu filtra por ela, e a
// guarda de rota usa a mesma matriz — então nunca aparece item que, ao
// clicar, dá "sem acesso".
const itens = [
  { to: '/maps',         area: 'leads',       icon: <MapPin size={16} />,          label: 'Extrator Maps'      },
  { to: '/instagram',    area: 'leads',       icon: <Camera size={16} />,          label: 'Extrator Instagram' },
  { to: '/meus-leads',   area: 'leads',       icon: <Target size={16} />,          label: 'Meus Leads'         },
  { to: '/calculadora',  area: 'calculadora', icon: <Calculator size={16} />,      label: 'Calculadora'        },
  { to: '/disparo',      area: 'disparo',     icon: <Send size={16} />,            label: 'Disparos'           },
  { to: '/whatsapp',     area: 'whatsapp',    icon: <MessageCircle size={16} />,   label: 'WhatsApp'           },
  { to: '/kanban',       area: 'kanban',      icon: <LayoutGrid size={16} />,      label: 'Kanban'             },
  { to: '/clientes',     area: 'clientes',    icon: <Users size={16} />,           label: 'Clientes'           },
  { to: '/carteira',     area: 'carteira',    icon: <FileSpreadsheet size={16} />, label: 'Atualizar carteira' },
  { to: '/relatorios',   area: 'relatorios',  icon: <BarChart2 size={16} />,       label: 'Relatórios'         },
  { to: '/migrador',     area: 'migrador',    icon: <RefreshCw size={16} />,       label: 'Migrador'           },
  { to: '/equipe',       area: 'equipe',      icon: <UserCog size={16} />,         label: 'Equipe'             },
  { to: '/config',       area: 'config',      icon: <Settings size={16} />,        label: 'Configurações'      },
];

export const ALTURA_TOPO = 52;

// `topo`: o mesmo menu, deitado numa barra em cima. Usado na tela do
// WhatsApp, que precisa da largura inteira para a lista e a conversa.
export default function Sidebar({ topo = false }) {
  const { user, logout, papel, pode, ehDono, vinculo } = useAuth();
  const [modalLogin, setModalLogin] = useState(false);

  // Deslogado continua vendo o menu inteiro (é a vitrine do produto).
  // Logado vê só o que o papel dele permite.
  const visiveis = user ? itens.filter(i => pode(i.area)) : itens.filter(i => i.area !== 'equipe');

  if (topo) {
    return (
      <>
        <header style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: ALTURA_TOPO + 'px', zIndex: 50,
          background: '#ffffff', borderBottom: '1px solid rgba(0,0,0,0.07)',
          display: 'flex', alignItems: 'center', gap: '10px', padding: '0 14px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '8px',
              background: 'linear-gradient(135deg, #6c47ff, #5535e0)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: '800', fontSize: '12px'
            }}>FL</div>
            <span style={{ fontWeight: '700', fontSize: '15px' }}>
              Fa<span style={{ color: '#6c47ff' }}>Lei</span>
            </span>
          </div>

          <nav style={{ flex: 1, display: 'flex', gap: '2px', overflowX: 'auto', minWidth: 0 }}>
            {visiveis.map(item => (
              <NavLink key={item.to} to={item.to} title={item.label}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0,
                  padding: '7px 10px', borderRadius: '8px',
                  textDecoration: 'none', fontSize: '12.5px', fontWeight: '500', whiteSpace: 'nowrap',
                  color: isActive ? '#6c47ff' : '#52526e',
                  background: isActive ? 'rgba(108,71,255,0.07)' : 'transparent',
                })}>
                {item.icon}
                {item.label}
              </NavLink>
            ))}
          </nav>

          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <span title={user.email + (!ehDono && vinculo?.donoEmail ? ' · conta de ' + vinculo.donoEmail : '')}
                style={{ fontSize: '10px', fontWeight: '700', fontFamily: 'monospace',
                  padding: '2px 8px', borderRadius: '5px',
                  background: corPapel(papel) + '18', color: corPapel(papel) }}>
                {labelPapel(papel).toUpperCase()}
              </span>
              <button onClick={logout} title="Sair"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', border: 'none', background: 'none',
                  cursor: 'pointer', fontSize: '12.5px', color: '#dc2626', fontWeight: '500', padding: '6px' }}>
                <LogOut size={15} /> Sair
              </button>
            </div>
          ) : (
            <button onClick={() => setModalLogin(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', border: 'none', cursor: 'pointer',
                fontSize: '12.5px', fontWeight: '700', color: '#fff', padding: '7px 12px', borderRadius: '8px',
                background: 'linear-gradient(135deg, #6c47ff, #5535e0)' }}>
              <LogIn size={14} /> Entrar
            </button>
          )}
        </header>
        {modalLogin && <LoginModal onClose={() => setModalLogin(false)} />}
      </>
    );
  }

  return (
    <>
      <aside style={{
        width: '220px', minHeight: '100vh', background: '#ffffff',
        borderRight: '1px solid rgba(0,0,0,0.07)',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 50
      }}>
        {/* Logo */}
        <div style={{ padding: '20px 20px 16px',
          borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '9px',
              background: 'linear-gradient(135deg, #6c47ff, #5535e0)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: '800', fontSize: '13px'
            }}>FL</div>
            <span style={{ fontWeight: '700', fontSize: '16px' }}>
              Fa<span style={{ color: '#6c47ff' }}>Lei</span>
            </span>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '10px', overflowY: 'auto' }}>
          {visiveis.map(item => (
            <NavLink key={item.to} to={item.to}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '9px 12px', borderRadius: '9px', marginBottom: '2px',
                textDecoration: 'none', fontSize: '13px', fontWeight: '500',
                color: isActive ? '#6c47ff' : '#52526e',
                background: isActive ? 'rgba(108,71,255,0.07)' : 'transparent',
                transition: 'all 0.15s'
              })}>
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Footer — login/logout */}
        <div style={{ padding: '12px 10px',
          borderTop: '1px solid rgba(0,0,0,0.07)' }}>
          {user ? (
            <div>
              {/* Em qual conta estou trabalhando — quem entra como equipe
                  precisa enxergar isso, senão confunde a carteira do patrão
                  com a própria. */}
              {!ehDono && vinculo?.donoEmail && (
                <div style={{ fontSize: '10px', color: '#6c47ff', background: 'rgba(108,71,255,0.07)',
                  borderRadius: '7px', padding: '6px 10px', marginBottom: '6px', lineHeight: 1.4 }}>
                  Conta de<br />
                  <span style={{ fontFamily: 'monospace', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                    {vinculo.donoEmail}
                  </span>
                </div>
              )}
              <div style={{ fontSize: '11px', color: '#9898b8',
                fontFamily: 'monospace', padding: '4px 12px',
                marginBottom: '4px', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.email}
              </div>
              <div style={{ padding: '0 12px', marginBottom: '6px' }}>
                <span style={{ fontSize: '10px', fontWeight: '700', fontFamily: 'monospace',
                  padding: '2px 8px', borderRadius: '5px',
                  background: corPapel(papel) + '18', color: corPapel(papel) }}>
                  {labelPapel(papel).toUpperCase()}
                </span>
              </div>
              <button onClick={logout}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  width: '100%', padding: '9px 12px', borderRadius: '9px',
                  border: 'none', background: 'none', cursor: 'pointer',
                  fontSize: '13px', color: '#dc2626', fontWeight: '500',
                  fontFamily: 'DM Sans, sans-serif'
                }}>
                <LogOut size={15} /> Sair
              </button>
            </div>
          ) : (
            <button onClick={() => setModalLogin(true)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '8px', width: '100%', padding: '10px 12px', borderRadius: '9px',
                border: 'none', cursor: 'pointer', fontSize: '13px',
                fontWeight: '700', color: '#fff', fontFamily: 'DM Sans, sans-serif',
                background: 'linear-gradient(135deg, #6c47ff, #5535e0)',
                boxShadow: '0 4px 12px rgba(108,71,255,0.3)'
              }}>
              <LogIn size={15} /> Entrar
            </button>
          )}
        </div>
      </aside>

      {modalLogin && <LoginModal onClose={() => setModalLogin(false)} />}
    </>
  );
}
